import type * as React from 'react'
import { useRef, useState } from 'react'

import { UploadSource } from '@buffer-mono/uploader'
import type { Uploader } from '@buffer-mono/uploader'

import { MAX_ATTACHMENTS, MAX_FILE_SIZE_MB } from '../FeedbackWidget.constants'
import type { Attachment } from '../FeedbackWidget.types'

interface UseAttachmentsOptions {
  uploader: Uploader
  setError: (error: string | null) => void
}

interface UseAttachmentsReturn {
  attachments: Attachment[]
  isDragging: boolean
  hasUploadingAttachments: boolean
  fileInputRef: React.RefObject<HTMLInputElement>
  handleFileSelect: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>
  handleDragEnter: (event: React.DragEvent<HTMLDivElement>) => void
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void
  handleDragLeave: (event: React.DragEvent<HTMLDivElement>) => void
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>
  handleRemoveAttachment: (attachmentId: string) => void
  handleAttachClick: () => void
  cleanupAttachments: () => void
}

const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
  event.preventDefault()
  event.stopPropagation()
  event.dataTransfer.dropEffect = 'copy'
}

export function useAttachments({
  uploader,
  setError,
}: UseAttachmentsOptions): UseAttachmentsReturn {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [isDragging, setIsDragging] = useState(false)

  const hasUploadingAttachments = attachments.some((a) => a.isUploading)

  const processFiles = async (files: FileList | File[]): Promise<void> => {
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return

    const remainingSlots = MAX_ATTACHMENTS - attachments.length
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_ATTACHMENTS} attachments allowed`)
      return
    }

    const newAttachments: Attachment[] = []
    const filesToUpload: File[] = []

    for (let i = 0; i < Math.min(fileArray.length, remainingSlots); i++) {
      const file = fileArray[i]

      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`)
        continue
      }

      // Check file type
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')

      if (!isImage && !isVideo) {
        setError(`File "${file.name}" is not a supported image or video format`)
        continue
      }

      newAttachments.push({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        file,
        previewUrl: URL.createObjectURL(file),
        type: isImage ? 'image' : 'video',
        isUploading: true,
      })
      filesToUpload.push(file)
    }

    if (newAttachments.length > 0) {
      setAttachments((prev) => [...prev, ...newAttachments])
      setError(null)

      // Upload files to S3 and update attachment state with URLs
      try {
        const result = await uploader.upload(filesToUpload, {
          source: UploadSource.filePicker(),
        })

        // Update attachments with uploaded URLs
        if (result.successful.length > 0) {
          setAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = result.successful.find(
                (u) => u.name === attachment.file.name,
              )
              if (uploaded) {
                return {
                  ...attachment,
                  uploadedUrl: uploaded.uploadURL,
                  isUploading: false,
                }
              }
              return attachment
            }),
          )
        }

        // Handle failed uploads
        if (result.failed.length > 0) {
          setAttachments((prev) =>
            prev.map((attachment) => {
              const failed = result.failed.find(
                (f) => f.name === attachment.file.name,
              )
              if (failed) {
                return {
                  ...attachment,
                  isUploading: false,
                  uploadError: 'Upload failed',
                }
              }
              return attachment
            }),
          )
          setError('Some files failed to upload')
        }

        // Reset uppy instance for next upload
        uploader.uppyInstance.cancelAll()
      } catch {
        // Mark all new attachments as failed
        setAttachments((prev) =>
          prev.map((attachment) => {
            if (filesToUpload.some((f) => f.name === attachment.file.name)) {
              return {
                ...attachment,
                isUploading: false,
                uploadError: 'Upload failed',
              }
            }
            return attachment
          }),
        )
        setError('Failed to upload files')
        uploader.uppyInstance.cancelAll()
      }
    }
  }

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = event.target.files
    if (!files) return

    await processFiles(files)

    // Reset input so the same file can be selected again
    event.target.value = ''
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()

    if (dragCounterRef.current < 0) {
      dragCounterRef.current = 0
    }
    dragCounterRef.current += 1

    if (dragCounterRef.current === 1) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    event.stopPropagation()

    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)

    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDrop = async (
    event: React.DragEvent<HTMLDivElement>,
  ): Promise<void> => {
    event.preventDefault()
    event.stopPropagation()

    dragCounterRef.current = 0
    setIsDragging(false)

    const files = event.dataTransfer.files
    if (files.length > 0) {
      await processFiles(files)
    }
  }

  const handleRemoveAttachment = (attachmentId: string): void => {
    setAttachments((prev) => {
      const attachment = prev.find((a) => a.id === attachmentId)
      if (attachment) {
        URL.revokeObjectURL(attachment.previewUrl)
      }
      return prev.filter((a) => a.id !== attachmentId)
    })
  }

  const handleAttachClick = (): void => {
    fileInputRef.current?.click()
  }

  const cleanupAttachments = (): void => {
    attachments.forEach((attachment) => {
      URL.revokeObjectURL(attachment.previewUrl)
    })
    setAttachments([])
    setIsDragging(false)
    dragCounterRef.current = 0
  }

  return {
    attachments,
    isDragging,
    hasUploadingAttachments,
    fileInputRef,
    handleFileSelect,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveAttachment,
    handleAttachClick,
    cleanupAttachments,
  }
}
